import { useState } from 'react'
import { Modal, Button, Group, Stack, TextInput, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useMutation } from '@apollo/client/react'
import AddRoom from '../graphql/queries/AddRoom'
import SetAssistant from '../graphql/queries/SetAssistant'
import CurrentUser from '../graphql/queries/CurrentUser'
import { useModalStack } from '../ui/ModalStack'
import TagStringEditor from '../ui/TagStringEditor'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'

export default function CreateRoomModal({ opened, onClose, ...rest }) {
    const isMobile = useMediaQuery('(max-width: 50em)')
    const { pushModal } = useModalStack()
    const dispatch = useAppDispatch()
    const [addRoomMutation, { loading }] = useMutation(AddRoom, {
        refetchQueries: [{ query: CurrentUser }]
    })
    const [setAssistantMutation] = useMutation(SetAssistant)

    const [name, setName] = useState('')
    const [topic, setTopic] = useState('')
    const [spinning, setSpinning] = useState('')
    const [selectedDJ, setSelectedDJ] = useState(null)

    async function handleCreateRoom() {
        const result = await addRoomMutation({ variables: { data: { name, topic, spinning } } })
        const newRoomId = result.data?.addRoom?.id
        
        // Set the assistant if one was selected
        if (selectedDJ && selectedDJ.username && newRoomId) {
            await setAssistantMutation({
                variables: {
                    roomId: newRoomId,
                    username: selectedDJ.username
                }
            })
        }
        
        if (newRoomId) {
            dispatch(Actions.setCurrentRoom(newRoomId))
        }
        onClose()
        // Reset form
        setName('')
        setTopic('')
        setSpinning('')
        setSelectedDJ(null)
    }

    function handleClose() {
        onClose()
        // Reset form when closing without creating
        setName('')
        setTopic('')
        setSpinning('')
        setSelectedDJ(null)
    }

    function handlePickDJ() {
        pushModal('djChooser', {
            initialDJ: selectedDJ,
            onSelectDJ: (dj) => {
                setSelectedDJ(dj)
            }
        })
    }

    const isNameEmpty = !name.trim()

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Create Room"
            size="md"
            centered
            fullScreen={isMobile}
            {...rest}
            styles={{
                title: {
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }
            }}
        >
            <Stack gap="md" style={{ marginTop: '1em' }}>
                <TextInput
                    label="Name"
                    placeholder="Enter room name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                <TextInput
                    label="Topic"
                    placeholder="Enter room topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                />

                <Stack gap="xs">
                    <Text size="sm" fw={500}>DJ</Text>
                    <Group gap="sm" align="center">
                        <Text size="sm" c="dimmed">
                            {selectedDJ ? selectedDJ.name : 'No DJ selected'}
                        </Text>
                        <Button
                            size="xs"
                            variant="light"
                            onClick={handlePickDJ}
                        >
                            Pick a DJ
                        </Button>
                    </Group>
                </Stack>

                <Stack gap={5}>
                    <Text size="sm" fw={500}>
                        What kind of music would you like to spin in this room?
                    </Text>
                    <TagStringEditor
                        value={spinning}
                        onChange={setSpinning}
                        placeholder="e.g., 'rock', 'jazz or blues', 'happy indie'"
                    />
                </Stack>

                <Group justify="flex-end" mt="md">
                    <Button
                        variant="subtle"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateRoom}
                        loading={loading}
                        disabled={isNameEmpty}
                    >
                        Create Room
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}

